import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Camera, Check, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScannerPage() {
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [manualId, setManualId] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scannerRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (scannerRef.current) {
      try { scannerRef.current.stop(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScanner = async () => {
    setScanResult(null);
    setCheckedIn(false);
    setScanning(true);

    try {
      // Dynamic import
      const { Html5Qrcode } = await import("html5-qrcode");
      
      // Stop any existing scanner
      if (scannerRef.current) {
        try { await scannerRef.current.stop(); } catch {}
      }

      const scanner = new Html5Qrcode("qr-reader-container");
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 }, aspectRatio: 1.0 },
        async (decodedText) => {
          // Stop scanner after successful scan
          try { await scanner.stop(); } catch {}
          scannerRef.current = null;
          setScanning(false);

          // Process the scan
          try {
            const res = await axios.post(`${API}/tickets/scan?qr_data=${encodeURIComponent(decodedText)}`);
            setScanResult(res.data);
          } catch (err) {
            setScanResult({
              valid: false,
              message: err.response?.data?.detail || "Invalid ticket",
              ticket: null,
              event: null
            });
          }
        },
        () => {} // ignore errors during scanning
      );
    } catch (err) {
      console.error("Scanner error:", err);
      setScanning(false);
      toast.error("Could not access camera. Please allow camera permissions in your browser settings, or use manual entry below.");
    }
  };

  const handleManualScan = async () => {
    if (!manualId.trim()) return;
    const qrData = manualId.startsWith("BCTICKET:") ? manualId : `BCTICKET:${manualId.trim()}`;
    try {
      const res = await axios.post(`${API}/tickets/scan?qr_data=${encodeURIComponent(qrData)}`);
      setScanResult(res.data);
    } catch (err) {
      setScanResult({
        valid: false,
        message: err.response?.data?.detail || "Invalid ticket ID",
        ticket: null, event: null
      });
    }
  };

  const handleCheckin = async () => {
    if (!scanResult?.ticket?.id) return;
    try {
      await axios.post(`${API}/tickets/${scanResult.ticket.id}/checkin`);
      setCheckedIn(true);
      toast.success("Checked in!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Check-in failed");
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-32" data-testid="scanner-page">
      <div className="max-w-lg mx-auto px-6">
        <div className="text-center mb-8">
          <h1 className="font-subheading text-2xl uppercase tracking-tight mb-2">Ticket Scanner</h1>
          <p className="font-body text-sm text-zinc-500">Scan attendee QR codes for check-in</p>
        </div>

        {/* Scanner Area */}
        <div className="border border-white/10 overflow-hidden mb-6">
          <div id="qr-reader-container" style={{ width: "100%", minHeight: scanning ? "300px" : "0" }} />
          {!scanning && !scanResult && (
            <div className="p-12 text-center">
              <Camera size={48} className="text-zinc-600 mx-auto mb-4" />
              <button
                onClick={startScanner}
                data-testid="start-scanner-btn"
                className="font-subheading text-sm uppercase tracking-widest bg-white text-black px-8 py-3.5 hover:bg-zinc-200 transition-colors"
              >
                Open Camera Scanner
              </button>
            </div>
          )}
          {scanning && (
            <div className="p-4 text-center">
              <p className="font-body text-xs text-zinc-500 animate-pulse">Point camera at QR code...</p>
              <button onClick={stopCamera} className="font-body text-xs text-zinc-600 mt-2 underline">Stop Camera</button>
            </div>
          )}
        </div>

        {/* Manual Entry Fallback */}
        <div className="border border-white/10 p-5 mb-6">
          <p className="font-subheading text-xs uppercase tracking-wider text-zinc-500 mb-3">Manual Entry (if camera unavailable)</p>
          <div className="flex gap-2">
            <input
              value={manualId}
              onChange={e => setManualId(e.target.value)}
              className="bc-input flex-1"
              placeholder="Enter ticket ID"
              data-testid="manual-ticket-id"
            />
            <button
              onClick={handleManualScan}
              data-testid="manual-scan-btn"
              className="font-subheading text-xs uppercase tracking-widest bg-white text-black px-5 py-2.5 hover:bg-zinc-200 transition-colors"
            >
              Check
            </button>
          </div>
        </div>

        {/* Scan Result */}
        {scanResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`border p-6 mb-6 ${
              checkedIn
                ? "border-green-500/30 bg-green-500/10"
                : scanResult.valid
                ? "border-blue-500/30 bg-blue-500/10"
                : "border-red-500/30 bg-red-500/10"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              {checkedIn ? (
                <Check size={28} className="text-green-400" />
              ) : scanResult.valid ? (
                <Check size={28} className="text-blue-400" />
              ) : (
                <AlertTriangle size={28} className="text-red-400" />
              )}
              <h3 className={`font-subheading text-lg uppercase tracking-wider ${
                checkedIn ? "text-green-400" : scanResult.valid ? "text-blue-400" : "text-red-400"
              }`}>
                {checkedIn ? "CHECKED IN" : scanResult.message}
              </h3>
            </div>

            {scanResult.ticket && (
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="font-body text-xs text-zinc-500 uppercase">Name</span>
                  <span className="font-body text-sm text-white">{scanResult.ticket.attendee_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-body text-xs text-zinc-500 uppercase">Type</span>
                  <span className="font-body text-sm text-white">{scanResult.ticket.ticket_type}</span>
                </div>
                {scanResult.event && (
                  <div className="flex justify-between">
                    <span className="font-body text-xs text-zinc-500 uppercase">Event</span>
                    <span className="font-body text-sm text-white">{scanResult.event.title}</span>
                  </div>
                )}
              </div>
            )}

            {scanResult.valid && !checkedIn && (
              <button
                onClick={handleCheckin}
                data-testid="checkin-btn"
                className="w-full font-subheading text-sm uppercase tracking-widest bg-green-500 text-black px-6 py-3 hover:bg-green-400 transition-colors flex items-center justify-center gap-2"
              >
                <Check size={16} /> Confirm Check-In
              </button>
            )}
          </motion.div>
        )}

        {/* Scan Again */}
        {scanResult && (
          <button
            onClick={() => { setScanResult(null); setCheckedIn(false); startScanner(); }}
            data-testid="scan-again-btn"
            className="w-full font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-3 hover:border-white hover:bg-white/5 transition-all flex items-center justify-center gap-2"
          >
            <RotateCcw size={14} /> Scan Next Ticket
          </button>
        )}
      </div>
    </div>
  );
}
